// Short game guide with every power-up, opened from the start page (Design §9.1).
import { el, openWindow } from './dom.js';
import { LocalGame } from '../pkg/client_wasm.js';
import { BAD_COLOR, type Constants, GOOD_COLOR, POWERUPS } from './render.js';
import {
  AUTUMN,
  GRASS,
  HEAT,
  HEDGE,
  HILL,
  ICE,
  LAKE,
  LEAVES,
  MUD,
  PUDDLE,
  SAND,
  SNOW,
  SPRING,
  SUMMER,
  WATER,
  WINTER,
  drawCover,
  drawHeat,
  drawHoles,
  drawPatches,
  drawWater,
  drawRain,
  drawSnowfall,
  drawWind,
  patchesOf,
  floorColor,
  nearestPatch,
} from './seasons.js';
import { SHOWN, SLOTS, partCard } from './shop.js';

/** A group that opens on click, closed at first; `items` framed like the shop parts. */
function group(title: string, items: HTMLElement[]): HTMLElement {
  return el('details', {}, el('summary', { textContent: title }), el('div', { className: 'items' }, ...items));
}

function powerups(title: string, good: boolean): HTMLElement {
  return group(
    title,
    POWERUPS.filter((p) => p.good === good).map((p) => {
      const icon = el('span', { className: 'icon', textContent: p.icon });
      icon.style.background = good ? GOOD_COLOR : BAD_COLOR;
      return el('div', { className: 'item up' }, icon, el('span', {}, el('b', { textContent: p.name }), el('div', { textContent: p.text })));
    }),
  );
}

/** Every shop component per slot, in shop order, with its upside and downside. */
function parts(): HTMLElement[] {
  return SHOWN.map((slot) =>
    group(
      SLOTS[slot].title,
      SLOTS[slot].parts.map((_, c) => partCard('div', slot, c, { className: 'item' })),
    ),
  );
}

/** What a card's picture shows: the patch of a kind nearest the middle, or a weather over open floor. */
type Show = number | 'fog' | 'rain' | 'wind' | 'snowfall';

/** One effect: name, how it works, its picture. */
type Effect = [string, string, Show];

const HILLS: Effect = ['Hills', 'up to 40% slower uphill, faster downhill; bullets fired from below only fly up to the top and get stuck there – whoever stands behind it is safe', HILL];

/** Per season (Design §4.1): everything it brings, with how it works. */
const SEASONS: { season: number; name: string; effects: Effect[] }[] = [
  {
    season: SPRING,
    name: 'Spring',
    effects: [
      ['Fog banks', 'drifting fog clouds: whoever is inside is invisible to the others and can only see 3 tank lengths far', 'fog'],
      ['Rain showers', 'every 25 s for 9 s: everyone slides more (not the Hover), bullets fly less far', 'rain'],
      ['Hedges', 'slow to half speed (Tracks+ less, not the Hover); bullets get stuck in them', HEDGE],
      ['Brook', 'water right across the arena, with two fords: whoever drives in with the middle of the tank sinks (not the Hover)', WATER],
      HILLS,
    ],
  },
  {
    season: SUMMER,
    name: 'Summer',
    effects: [
      ['Sand', 'slows to 70% (Tracks+ barely, Sport more)', SAND],
      ['Heat shimmer', 'whoever stands in it shoots with double spread', HEAT],
      ['Tall grass', 'whoever stands in it is invisible to the others – except for 2 s after their own shot', GRASS],
      ['Pond', 'water: whoever drives in with the middle of the tank sinks (not the Hover)', WATER],
      HILLS,
    ],
  },
  {
    season: AUTUMN,
    name: 'Autumn',
    effects: [
      ['Mud', 'slows to half speed (Tracks+ barely, Sport more)', MUD],
      ['Leaf piles', 'hide tanks halfway and mines of others completely, even from the Radar', LEAVES],
      ['Puddles', 'bullets in them fly at only 55% speed', PUDDLE],
      ['Gusts', 'crosswind (wind vane top left): blows bullets up to two tank widths off course and pushes Hovers', 'wind'],
      HILLS,
    ],
  },
  {
    season: WINTER,
    name: 'Winter',
    effects: [
      ['Ice', 'very slippery (Tracks+ half as much)', ICE],
      ['Deep snow', 'slows to 65% (Tracks+ barely, Sport more); leaves ruts', SNOW],
      ['Frozen lake', 'slippery as ice; explosions break holes in it – whoever stands there drowns (a kill for the shooter), whoever drives in later sinks', LAKE],
      ['Snowfall', 'falling flakes, just for looks', 'snowfall'],
      ['Blizzard', 'crosswind like the gusts in autumn', 'wind'],
      HILLS,
    ],
  },
];

const PICTURE_SEED = 7; // the same sample arena in every season
const CLOSE_UP = 140; // world units a card's picture shows at least
const CARD_PX = 84; // CSS px, its size
const WEATHER_DENSITY = 8; // rain, snow and wind streaks thicker than in the game, so a close-up shows them
const SNOW_CLOSE_UP = 80; // world units, nearer for the small flakes

/** Draws a picture at time `now` (ms). */
type Painter = (now: number) => void;

/** The sample arena of a season with all its effects: what the close-ups need of it. */
interface Sample {
  width: number;
  height: number;
  season: number;
  terrain: Float32Array;
  fogBanks: Float32Array;
  wind: Float32Array;
}

/** Middle and radius of the first fog bank (layout as walls). */
function firstFogBank(banks: Float32Array): [number, number, number] | undefined {
  const corners = banks[0];
  if (!corners) return undefined;
  let [x, y, r] = [0, 0, 0];
  for (let k = 0; k < corners; k++) [x, y] = [x + banks[1 + 2 * k] / corners, y + banks[2 + 2 * k] / corners];
  for (let k = 0; k < corners; k++) r = Math.max(r, Math.hypot(banks[1 + 2 * k] - x, banks[2 + 2 * k] - y));
  return [x, y, r];
}

/** A close-up of one object of the sample arena alone on the floor (`show` a patch kind; the brook with its next pieces), or of the weather in the top left corner, by the vane. */
function closeUp(frame: Sample, show: Show, c: Constants): [HTMLCanvasElement, Painter] {
  const canvas = el('canvas', { className: 'sample' });
  const [w, h] = [frame.width, frame.height];
  const all = frame.terrain;
  const near = typeof show === 'number' ? nearestPatch(all, show, w / 2, h / 2) : undefined;
  const terrain = show === WATER ? patchesOf(all, WATER) : (near?.patch ?? new Float32Array());
  const spot = near ? [near.x, near.y, near.r] : show === 'fog' ? firstFogBank(frame.fogBanks) : undefined;
  const [x, y, r] = spot ?? [0, 0, 0];
  const size = show === 'snowfall' ? SNOW_CLOSE_UP : Math.min(Math.max(CLOSE_UP, 2.4 * r), w, h);
  const [left, top] = [Math.min(Math.max(x - size / 2, 0), w - size), Math.min(Math.max(y - size / 2, 0), h - size)];
  // The lake with a hole broken in, off its middle.
  const holes = show === LAKE ? Float32Array.of(x + r * 0.3, y) : new Float32Array();
  canvas.width = canvas.height = Math.round(CARD_PX * (window.devicePixelRatio || 1));
  return [
    canvas,
    (now) => {
      const ctx = canvas.getContext('2d')!;
      const scale = canvas.width / size;
      ctx.setTransform(scale, 0, 0, scale, -left * scale, -top * scale);
      ctx.fillStyle = floorColor(frame.season);
      ctx.fillRect(0, 0, w, h);
      drawPatches(ctx, terrain);
      drawWater(ctx, terrain, now);
      if (holes.length) drawHoles(ctx, holes, c.holeRadius, now);
      drawHeat(ctx, terrain, now);
      drawCover(ctx, terrain, show === 'fog' ? frame.fogBanks : undefined, now);
      if (show === 'rain') drawRain(ctx, size, size, now, WEATHER_DENSITY);
      if (show === 'snowfall') drawSnowfall(ctx, size, size, now, WEATHER_DENSITY);
      if (show === 'wind') drawWind(ctx, frame.wind, frame.season, size, size, now, WEATHER_DENSITY);
    },
  ];
}

/** A season: each effect with its close-up; drawn only while open. */
function seasonGroup(s: (typeof SEASONS)[number], c: Constants): HTMLElement {
  const game = LocalGame.preview(PICTURE_SEED, 4, s.season);
  const frame: Sample = {
    width: game.world_width(),
    height: game.world_height(),
    season: s.season,
    terrain: game.terrain(),
    fogBanks: game.fog_banks(),
    wind: Float32Array.from(game.wind()),
  };
  game.free();
  const painters: Painter[] = [];
  const cards = s.effects.map(([name, text, show]) => {
    const [canvas, paint] = closeUp(frame, show, c);
    painters.push(paint);
    return el('div', { className: 'item effect' }, canvas, el('span', {}, el('b', { textContent: name }), el('div', { textContent: text })));
  });
  const group = el('details', { className: 'season' }, el('summary', { textContent: s.name }), el('div', { className: 'items' }, ...cards));
  const loop = (now: number) => {
    if (!group.isConnected) return;
    if (group.open) for (const paint of painters) paint(now);
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);
  return group;
}

const seasons = (c: Constants) => SEASONS.map((s) => seasonGroup(s, c));

const list = (...items: string[]) => el('ul', {}, ...items.map((t) => el('li', { textContent: t })));

export function openGuide(c: Constants): void {
  if (document.getElementById('guide')) return;
  const panel = el('div', { id: 'guide', className: 'panel' });
  const close = openWindow(panel);
  panel.replaceChildren(
    el(
      'div',
      { className: 'head' },
      el('h2', { textContent: 'How to play' }),
      el('button', { textContent: 'Close', onclick: close }),
    ),
    el('h3', { textContent: 'Goal' }),
    list(
      'Everyone against everyone. The last one left wins the round; a match has several rounds.',
      'Bullets bounce off obstacles and the edge – back at you too.',
      'After a while the zone shrinks; outside it you lose HP.',
      'Out first in a match round? As a ghost you place one piece of the season’s ground within 10 s: fly with the drive keys, Fire places a hedge (spring), sand (summer), ice (winter) or a puddle (autumn, no season) – a tank length away from every tank.',
    ),
    el('h3', { textContent: 'Teams' }),
    list(
      'With teams on in the lobby (2, 3 or 4): the last team standing wins the round. Pick your team in the player list; the host can move players, shuffle, put people against bots or balance by team rating.',
      'Friendly fire is on: your bullets, blasts and mines hurt teammates too. A team kill costs 1 point.',
      'Everyone scores and ranks as without teams. The team with a tank left wins the round. The first team to the set number of round wins takes the match; if the round limit comes first, the most wins take it, level teams all win. No bounty, no underdog bonus.',
      'The tracks of each tank show its team color; teammates hiding in fog, grass or a cloak stay faintly visible to you, and so do their mines.',
    ),
    el('h3', { textContent: 'Controls' }),
    list(
      'Drive and turn: WASD or arrow keys',
      'Shoot: Space or Enter',
      'Use power-up: E or Shift',
      'Two players locally: Player 1 WASD, Space, E · Player 2 arrow keys, Enter, right Shift/Ctrl/Alt (Mac: Option) or Num 0',
      'Controller (Xbox, PlayStation): left stick or D-pad drives, A/✕ or RT/R2 shoots, B/○ or LT/L2 uses the power-up, Start/Options finishes the shop. Locally the first controller is player 1, the second player 2.',
      'Touch screen (online): put a thumb down on the left half for a stick, ● shoots, ★ uses the power-up. During a match the map fills the screen.',
      'Emotes (online): keys 1–4 (GG, Oops, Hi, Thanks), on a controller Y/△ picks and X/□ sends, on a touch screen 💬. 💬 in the status bar hides the others’.',
    ),
    el('h3', { textContent: 'Points' }),
    list(
      'Place in the round: first place gets one point less than there are tanks in the game, each following place one less.',
      'Kill +1, self-kill −1.',
      'Bounty: whoever leads alone at the start of a round glows gold – every hit on them gives +1, at most 3 per round (Marked hits included). Not in a match of only two tanks (and no Marked power-up there either).',
    ),
    el('h3', { textContent: 'Shop' }),
    list(
      'Between rounds you swap cannon, armor, drive and ammo. Every part has upsides and downsides.',
      'A swap costs points – the same ones the match is scored by: tanks in the match − 1, each further swap of yours one more.',
      'With only two tanks the shop opens first after round 3.',
    ),
    ...parts(),
    el('h3', { textContent: 'Power-ups' }),
    el('p', {
      className: 'hint',
      textContent:
        'Drive over one to pick it up (only one at a time), the power-up key uses it. Red ones hit the opponent in front of you, otherwise the nearest one.',
    }),
    powerups('For you', true),
    powerups('Against opponents', false),
    el('h3', { textContent: 'Seasons' }),
    list(
      'In the lobby: seasons off, random (one per round) or in turn, plus 1, 2 or all terrain effects per round. Local: one random season with 2 effects.',
      'The effects lie as patches between the obstacles – you can steer around them.',
      'Water (brook, pond, holes in the ice): whoever drives in with the middle of the tank sinks. Hovers drive over it, bullets fly over it.',
      'Drives: Tracks+ gets through mud, snow and sand well; the Hover ignores the ground but gets pushed by the wind; Sport suffers the most.',
    ),
    ...seasons(c),
  );
}
